import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FrontDeskArrival, FrontDeskDeparture, Instant, IsoDate, Result } from '@lemontaigu/pms-core';
import { todayIsoParis } from '@lemontaigu/staff';
import { useFrontDesk } from './FrontDeskProvider.js';
import { fmtDayLabel, shiftDay } from './logic.js';
import { arrivalDueLabel, fmtEuros, hhmm, nightsLabel, roomsLabel, vegaReadLabel } from './frontDeskFormat.js';

// « Arrivées · Départs » on the phone (operator 2026-09-30): which rooms are
// still to arrive, and which departures still have something to collect.
//
// Forward-looking only — today and later, never a past day (the RPCs refuse
// one too). Rooms, nights, the amount: no names and no payment history, which
// is what makes it safe to show the whole team. The desk's full sheets stay on
// reception/bos.

const REFRESH_MS = 60_000;

type Load<T> = { state: 'loading' } | { state: 'error'; detail: string } | { state: 'ok'; rows: T[] };

function useBriefing<T>(read: (date: IsoDate) => Promise<Result<T[]>>, date: IsoDate): Load<T> {
  const [data, setData] = useState<Load<T>>({ state: 'loading' });
  // Only the latest read may land: the minute tick races the day buttons.
  const latest = useRef(0);

  const load = useCallback(
    async (quiet: boolean) => {
      const mine = ++latest.current;
      if (!quiet) setData({ state: 'loading' });
      const r = await read(date);
      if (mine !== latest.current) return;
      if (r.ok) setData({ state: 'ok', rows: r.value });
      else if (!quiet) setData({ state: 'error', detail: r.error.message ?? 'lecture impossible' });
    },
    [read, date],
  );

  useEffect(() => {
    void load(false);
    const t = window.setInterval(() => void load(true), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  return data;
}

function Section<T>({
  title,
  data,
  empty,
  children,
}: {
  title: string;
  data: Load<T>;
  empty: string;
  children: (rows: T[]) => ReactNode;
}) {
  return (
    <section className="fd-section" aria-label={title}>
      <h3 className="fd-title">
        {title} {data.state === 'ok' && <span className="fd-count">({data.rows.length})</span>}
      </h3>
      {data.state === 'loading' && <p className="staff-empty">Chargement…</p>}
      {data.state === 'error' && <p className="auth-err">Erreur : {data.detail}</p>}
      {data.state === 'ok' && (data.rows.length === 0 ? <p className="staff-empty">{empty}</p> : children(data.rows))}
    </section>
  );
}

const latestReadAt = (rows: { readAt: Instant | null }[]): Instant | null => rows.find((r) => r.readAt)?.readAt ?? null;

export function FrontDesk() {
  const briefing = useFrontDesk();
  const today = todayIsoParis() as IsoDate;
  const [day, setDay] = useState<IsoDate>(today);

  const readArrivals = useCallback((d: IsoDate) => briefing.notYetArrived(d), [briefing]);
  const readDepartures = useCallback((d: IsoDate) => briefing.departuresToCollect(d), [briefing]);
  const arrivals = useBriefing<FrontDeskArrival>(readArrivals, day);
  const departures = useBriefing<FrontDeskDeparture>(readDepartures, day);

  // The feed's last check of Vega, from whichever list has a row carrying it.
  const readAt =
    (arrivals.state === 'ok' ? latestReadAt(arrivals.rows) : null) ??
    (departures.state === 'ok' ? latestReadAt(departures.rows) : null);
  const read = vegaReadLabel(readAt, new Date());

  return (
    <div className="frontdesk">
      <div className="staff-daybar">
        <button
          type="button"
          onClick={() => setDay(shiftDay(day, -1) as IsoDate)}
          disabled={day <= today}
          aria-label="Jour précédent"
        >
          ‹
        </button>
        <button type="button" onClick={() => setDay(shiftDay(day, 1) as IsoDate)} aria-label="Jour suivant">
          ›
        </button>
        <span className="staff-daylabel">{fmtDayLabel(day)}</span>
        {day !== today && (
          <button type="button" className="staff-today" onClick={() => setDay(today)}>
            Aujourd’hui
          </button>
        )}
      </div>
      {read && <p className={read.stale ? 'fd-read is-stale' : 'fd-read'}>{read.text}</p>}

      {/* Departures first: they come first in the day (operator 2026-10-01). */}
      <Section title="À encaisser" data={departures} empty="Rien à encaisser ce jour-là.">
        {(rows) => (
          <ul className="fd-list">
            {rows.map((d, i) => (
              <li key={i} className="fd-row">
                <span className="fd-rooms">{roomsLabel(d.rooms)}</span>
                <span className="fd-nights">{nightsLabel(d.nights)}</span>
                <span className="fd-due is-owed">{fmtEuros(d.dueCents)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Non arrivés" data={arrivals} empty="Personne à attendre ce jour-là.">
        {(rows) => (
          <ul className="fd-list">
            {rows.map((a, i) => {
              const due = arrivalDueLabel(a.dueCents);
              const time = hhmm(a.expectedTime);
              return (
                <li key={i} className="fd-row">
                  <span className="fd-rooms">{roomsLabel(a.rooms)}</span>
                  <span className="fd-nights">{nightsLabel(a.nights)}</span>
                  <span className={due.owed ? 'fd-due is-owed' : 'fd-due'}>{due.text}</span>
                  <span className="fd-time">{time ? `vers ${time}` : '—'}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
